<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1100">
    <v-card>
      <v-card-title class="dialog-title">
        {{ $t('org-dashboard.health-drilldown-title') }}
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0 drilldown-body">
        <!-- KPI mini-cards -->
        <div v-if="kpiCards && kpiCards.length" class="drilldown-kpi-cards">
          <div v-for="(kpi, i) in kpiCards" :key="'kpi-'+i" class="drilldown-kpi-card">
            <v-icon small :color="kpi.color" class="mr-1">{{ kpi.icon }}</v-icon>
            <div class="drilldown-kpi-content">
              <span class="drilldown-kpi-value">{{ kpi.value }}</span>
              <span class="drilldown-kpi-label">{{ kpi.label }}</span>
            </div>
          </div>
        </div>

        <!-- Category tabs -->
        <v-tabs v-model="activeTab" background-color="transparent" class="drilldown-tabs" @change="onTabChange">
          <v-tab v-for="tab in tabs" :key="tab.key" class="drilldown-tab">
            <span class="drilldown-tab-dot" :style="{ backgroundColor: tab.color }"></span>
            {{ tab.label }}
          </v-tab>
        </v-tabs>

        <!-- Data table -->
        <div class="drilldown-table-wrap">
          <v-data-table
            :headers="tableHeaders"
            :items="items"
            :server-items-length="totalItems"
            :options.sync="internalOptions"
            :loading="loading"
            :no-data-text="$t('org-dashboard.health-no-data')"
            :footer-props="{
              'items-per-page-options': [10, 20, 50],
            }"
            @update:options="onOptionsChange"
            class="drilldown-table"
          >
            <template v-slot:item.identifier="{ item }">
              <a class="drilldown-link" @click.stop="$emit('charger-click', item.id)">{{ item.identifier }}</a>
            </template>

            <template v-slot:item.connected="{ item }">
              <v-icon small :color="item.connected ? '#22c55e' : '#ef4444'">
                {{ item.connected ? 'mdi-circle' : 'mdi-circle-outline' }}
              </v-icon>
            </template>

            <template v-slot:item.offlineSince="{ item }">
              {{ item.offlineSince ? formatDate(item.offlineSince) : $t('org-dashboard.health-never-connected') }}
            </template>

            <template v-slot:item.inMaintenance="{ item }">
              <v-chip x-small :color="item.inMaintenance ? '#3b82f6' : '#e5e7eb'" :text-color="item.inMaintenance ? 'white' : '#6b7280'">
                {{ item.inMaintenance ? 'Oui' : 'Non' }}
              </v-chip>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
const TABS = [
  { key: 'online',      color: '#22c55e', icon: 'mdi-check-circle' },
  { key: 'warning',     color: '#eab308', icon: 'mdi-alert' },
  { key: 'incident',    color: '#f97316', icon: 'mdi-alert-circle' },
  { key: 'critical',    color: '#ef4444', icon: 'mdi-clock' },
  { key: 'inactive',    color: '#9ca3af', icon: 'mdi-power-off' },
  { key: 'maintenance', color: '#9ca3af', icon: 'mdi-wrench' },
]

export default {
  name: 'DashboardHealthDrilldown',
  props: {
    value: { type: Boolean, default: false },
    category: { type: String, default: 'incident' },
    items: { type: Array, default: () => [] },
    totalItems: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    kpiCards: { type: Array, default: () => [] },
  },

  data() {
    return {
      activeTab: this.categoryToTabIndex(this.category),
      internalOptions: { page: 1, itemsPerPage: 20 },
    }
  },

  computed: {
    tabs() {
      return TABS.map(t => ({
        ...t,
        label: this.$t('org-dashboard.health-' + t.key),
      }))
    },

    tableHeaders() {
      return [
        { text: this.$t('org-dashboard.health-identifier'), value: 'identifier' },
        { text: this.$t('org-dashboard.health-location'), value: 'locationName' },
        { text: this.$t('org-dashboard.health-connected'), value: 'connected', sortable: false },
        { text: this.$t('org-dashboard.health-offline-since'), value: 'offlineSince' },
        { text: this.$t('org-dashboard.health-duration'), value: 'offlineDurationHuman', sortable: false },
        { text: this.$t('org-dashboard.health-evse-offline'), value: 'offlineEvses', sortable: false },
        { text: this.$t('org-dashboard.health-maintenance'), value: 'inMaintenance', sortable: false },
      ]
    },
  },

  watch: {
    category(val) {
      this.activeTab = this.categoryToTabIndex(val)
    },
  },

  methods: {
    categoryToTabIndex(cat) {
      const idx = TABS.findIndex(t => t.key === cat)
      return idx >= 0 ? idx : 1
    },

    onTabChange(tabIndex) {
      const tab = TABS[tabIndex]
      if (tab && tab.key !== this.category) {
        this.$emit('update:category', tab.key)
      }
    },

    onOptionsChange(options) {
      this.internalOptions = options
      this.$emit('update:options', options)
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(this.$i18n.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
}

.drilldown-body {
  height: 70vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.drilldown-tabs {
  flex-shrink: 0;
  flex-grow: 0;

  ::v-deep .v-tabs-bar {
    background: #f9fafb !important;
    border-bottom: 1px solid #e5e7eb;
  }

  ::v-deep .v-tab {
    text-transform: none;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    min-width: 100px;
  }

  ::v-deep .v-tab--active {
    font-weight: 600;
  }
}

.drilldown-tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
}

.drilldown-table-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ::v-deep .drilldown-table.v-data-table {
    flex: 1;
    min-height: 0;
    display: flex !important;
    flex-direction: column !important;
  }

  ::v-deep .v-data-table__wrapper {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  ::v-deep thead th {
    position: sticky !important;
    top: 0;
    z-index: 1;
    background: #f9fafb !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    color: #6b7280 !important;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    border-bottom: 1px solid #e5e7eb !important;
    padding: 8px 10px !important;

    span {
      font-size: 11px !important;
      font-weight: 600 !important;
    }
  }

  ::v-deep tbody td {
    font-size: 12px !important;
    color: #374151;
    border-bottom: 1px solid #f3f4f6 !important;
    padding: 6px 10px !important;
  }

  ::v-deep tbody tr:hover {
    background: #f9fafb !important;
  }

  ::v-deep tbody tr:last-child td {
    border-bottom: none !important;
  }

  ::v-deep .v-data-table__empty-wrapper {
    vertical-align: top;

    td {
      padding: 48px 16px !important;
      color: #9ca3af;
      font-size: 13px;
    }
  }

  ::v-deep .v-data-footer {
    flex-shrink: 0;
    border-top: 1px solid #e5e7eb;
    font-size: 12px;
  }
}

.drilldown-link {
  color: #1976d2;
  text-decoration: none;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
    color: #1565c0;
  }
}

.drilldown-kpi-cards {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  background: #f9fafb;
  flex-shrink: 0;
}

.drilldown-kpi-card {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  flex: 1;
  min-width: 0;
}

.drilldown-kpi-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.drilldown-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.drilldown-kpi-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
